import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  Avatar,
  Select,
  MenuItem,
  FormControl
} from '@mui/material';
import { ArrowBackIcon, DownloadIcon } from 'icons/materialIcons';
import { useAuth } from 'contexts/AuthContext';
import { useLeadCaptureContext } from 'contexts/LeadCaptureContext';
import { saveLead } from 'apis/leadCaptureApi';
import { getCardsByUser } from 'apis/userApi';
import avatarPlaceholder from 'images/avatar-cropped.png';

import LeadValidationUtils from './LeadValidationUtils';
import FileImportUtils from 'utils/contacts/FileImportUtils';
import { generateLeadImportTemplate } from './LeadFieldMappings';
import CSVReview from './CSVReview';
import RequiredFields from './RequiredFields';
import FileUploader from './FileUploader';

const ImportViaCSV = ({ onBack, onNotification }) => {
  const [csvData, setCsvData] = useState(null);
  const [columns, setColumns] = useState([]);
  const [step, setStep] = useState(1);
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importStatus, setImportStatus] = useState({
    total: 0,
    success: 0,
    failed: 0
  });
  const [importError, setImportError] = useState('');
  const [userCards, setUserCards] = useState([]);
  const [selectedCardId, setSelectedCardId] = useState('');
  const [loadingCards, setLoadingCards] = useState(true);

  const { authState } = useAuth();
  const { addContact, fetchContacts } = useLeadCaptureContext();

  // Fetch user cards on component mount
  useEffect(() => {
    const fetchUserCards = async () => {
      if (!authState?.user?.userId) return;

      try {
        setLoadingCards(true);
        const cards = await getCardsByUser(authState.user.userId);
        setUserCards(cards);

        // Set default card as selected
        const defaultCard = cards.find(card => card.is_default) || cards[0];
        if (defaultCard) {
          setSelectedCardId(defaultCard._id);
        }
      } catch (error) {
        setImportError('Failed to load cards. Please try again.');
      } finally {
        setLoadingCards(false);
      }
    };

    fetchUserCards();
  }, [authState?.user?.userId]);

  // File selection handler
  const handleFileSelect = useCallback(async (selectedFile) => {
    try {
      // Import file with improved parsing
      const { data, columns } = await FileImportUtils.importFile(selectedFile);

      // Filter out empty rows to ensure clean data
      const validRows = data.filter(row => {
        return Object.values(row).some(val =>
          val !== null && val !== undefined && val !== '');
      });

      setCsvData(validRows);
      setColumns(columns);
      setStep(2);
    } catch (error) {
      setImportError(error.message);
    }
  }, []);

  // Function to actually import the leads
  const importLeads = async (leads) => {
    if (!leads || !leads.length) {
      return;
    }

    // Check if user ID is available
    if (!authState || !authState.user || !authState.user.userId) {
      setImportError("User ID is not available. Please ensure you are logged in and try again.");
      return;
    }

    setIsImporting(true);
    setImportStatus({
      total: leads.length,
      success: 0,
      failed: 0
    });
    setStep(3);

    const results = {
      success: 0,
      failed: 0,
      errors: []
    };

    for (let i = 0; i < leads.length; i++) {
      const lead = leads[i];
      setImportProgress(Math.round(((i + 1) / leads.length) * 100));

      try {
        // Explicitly prepare lead data with user ID and selected card ID
        const leadData = {
          ...lead,
          leadSource: 'csv-import',
          userId: authState.user.userId,
          // Only include cardId if a card is selected
          ...(selectedCardId ? { cardId: selectedCardId } : {})
        };

        const response = await saveLead(leadData);

        if (response && response.success) {
          if (response.lead && response.lead._id) {
            // Ensure the lead has all required fields for UI display
            const contactToAdd = {
              ...response.lead,
              // Ensure these fields exist for UI compatibility
              leadName: response.lead.leadName || response.lead.fullName || '',
              leadEmail: response.lead.leadEmail || response.lead.email || '',
              leadPhoneNumber: response.lead.leadPhoneNumber || response.lead.phone || '',
              leadJobTitle: response.lead.leadJobTitle || response.lead.jobTitle || '',
              leadCompany: response.lead.leadCompany || response.lead.company || '',
              leadSource: response.lead.leadSource || 'csv-import',
              createdAt: response.lead.createdAt || new Date().toISOString()
            };

            addContact(contactToAdd);
            results.success++;
          } else {
            results.failed++;
            results.errors.push({
              email: leadData.leadEmail,
              error: 'Lead appears to be saved but no valid lead object returned'
            });
          }
        } else {
          results.failed++;
          results.errors.push({
            email: leadData.leadEmail,
            error: response?.message || 'Unknown error'
          });
        }
      } catch (error) {
        results.failed++;
        results.errors.push({
          email: lead.leadEmail || 'Unknown email',
          error: error.message || 'Unknown error'
        });
      }

      setImportStatus({
        total: leads.length,
        success: results.success,
        failed: results.failed
      });
    }

    setTimeout(async () => {
      setIsImporting(false);

      if (results.failed > 0) {
        const errorDetails = results.errors
          .slice(0, 5)
          .map(e => `• ${e.email}: ${e.error}`)
          .join('\n');

        const additionalErrors = results.errors.length > 5
          ? `\n• ...and ${results.errors.length - 5} more errors`
          : '';

        onNotification({
          open: true,
          message: `Import completed with ${results.success} successful and ${results.failed} failed imports. Check console for details.`,
          severity: 'warning'
        });
      } else {
        onNotification({
          open: true,
          message: `Successfully imported ${results.success} leads!`,
          severity: 'success'
        });
      }

      // Refresh the contacts list to ensure UI is updated
      if (authState?.user?.userId) {
        await fetchContacts(authState.user.userId);
      }

      onBack();
    }, 1000);
  };

  // Generate and download a template CSV
  const downloadTemplate = () => {
    const template = generateLeadImportTemplate();
    FileImportUtils.downloadTemplate(template, 'leads_import_template.csv');
  };

  // Render different content based on current step
  const renderContent = () => {
    if (step === 1) {
      return (
        <>
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box>
                <Typography variant="h6" sx={{ mb: 1 }}>How to format the CSV</Typography>
                <Typography sx={{ color: '#4F4F4F', fontSize: '13px' }}>
                  Take a look at the possible columns below or download the CSV template.
                </Typography>
              </Box>
              <Button
                startIcon={<DownloadIcon />}
                onClick={downloadTemplate}
                sx={{
                  color: '#29AEF8',
                  fontSize: '13px',
                  fontWeight: 600,
                  '&:hover': {
                    backgroundColor: 'transparent',
                    textDecoration: 'underline'
                  }
                }}
              >
                Download CSV Template
              </Button>
            </Box>
            <RequiredFields />
          </Box>
          <FileUploader onFileSelect={handleFileSelect} />
          {importError && (
            <Alert
              severity="error"
              sx={{ mt: 3 }}
            >
              {importError}
            </Alert>
          )}
        </>
      );
    }

    if (step === 2 && csvData && columns.length > 0) {
      return (
        <CSVReview
          csvData={csvData}
          columns={columns}
          initialMappings={{}}
          onComplete={importLeads}
          onCancel={() => setStep(1)}
          userCards={userCards}
          selectedCardId={selectedCardId}
          setSelectedCardId={setSelectedCardId}
          loadingCards={loadingCards}
        />
      );
    }

    if (step === 3) {
      return (
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '400px'
        }}>
          <Typography variant="h5" sx={{ mb: 4, fontWeight: 500 }}>
            Importing Leads
          </Typography>

          <Box sx={{ position: 'relative', display: 'inline-flex', mb: 3 }}>
            <CircularProgress
              variant="determinate"
              value={importProgress}
              size={120}
              thickness={5}
              sx={{ color: '#29AEF8' }}
            />
            <Box
              sx={{
                top: 0,
                left: 0,
                bottom: 0,
                right: 0,
                position: 'absolute',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography variant="h4" component="div" color="text.secondary">
                {`${importProgress}%`}
              </Typography>
            </Box>
          </Box>

          <Typography variant="h6" sx={{ mb: 1 }}>
            Processing {importStatus.success} of {importStatus.total} contacts
          </Typography>

          <Box sx={{ display: 'flex', gap: 4, mt: 2 }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h5" color="success.main">
                {importStatus.success}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Successful
              </Typography>
            </Box>

            {importStatus.failed > 0 && (
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h5" color="error">
                  {importStatus.failed}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Failed
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      );
    }

    return null;
  };

  return (
    <>
      <Box sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '24px',
        boxSizing: 'border-box'
      }}>
        <Box sx={{
          width: '100%',
          maxWidth: '1200px',
          height: 'auto',
          display: 'flex',
          padding: '42px',
          borderRadius: '18px',
          justifyContent: 'center',
          backgroundColor: '#ffffff',
          boxSizing: 'border-box'
        }}>
          <Box sx={{ width: '100%' }}>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 4
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <IconButton
                  onClick={onBack}
                  disabled={isImporting}
                  sx={{ color: '#4F4F4F', mr: 2 }}
                >
                  <ArrowBackIcon />
                </IconButton>
                <Typography variant="h5">
                  {step === 1 ? "Import leads" : `${csvData ? csvData.length : 0} ${csvData && csvData.length === 1 ? 'Lead' : 'Leads'} Found`}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <Box
                  sx={{
                    backgroundColor: step === 1 ? '#FFFFFF' : '#F7F7F7',
                    color: '#4F4F4F',
                    border: step === 1 ? '1px solid #E0E0E0' : 'none',
                    padding: '0 24px',
                    height: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    borderRadius: '100px',
                    fontSize: '12px',
                    fontWeight: 600
                  }}
                >
                  1 - Prepare & Upload
                </Box>
                <Box
                  sx={{
                    backgroundColor: step === 2 ? '#FFFFFF' : '#F7F7F7',
                    color: '#4F4F4F',
                    border: step === 2 ? '1px solid #E0E0E0' : 'none',
                    padding: '0 24px',
                    height: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    borderRadius: '100px',
                    fontSize: '12px',
                    fontWeight: 600
                  }}
                >
                  2 - Review
                </Box>
              </Box>
            </Box>

            {renderContent()}
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default ImportViaCSV;